/**
 *  Core Application class that orchestrates the LOA Frontend.
 * @module app
 */

import type { AppAction, AppConfig, AppInstance, AppVersion, UserCredentials } from "../types";
import { User } from "../types";

/**
 * Main Application class that serves as the central orchestrator for the LOA Frontend.
 *
 * @class Application
 * @see {AppInstance}
 * @module app
 * @category Application
 *
 * @remarks
 * The Application class follows a singleton-like pattern where a single instance
 * is created at application startup and shared via React Context. It manages:
 *
 * - **Authentication**: Login/logout operations
 * - **UI State**: Sidebar toggle and other UI interactions
 * - **Configuration**: App name, environment, and version info
 *
 * The class uses a dispatch function (from React's useReducer) to update
 * application state, ensuring all state changes flow through the reducer.
 *
 * @example
 * ```typescript
 * import { Application } from './app/Application';
 * import { getConfig } from './app/utils';
 *
 * const app = new Application(getConfig());
 *
 * // In a React component with access to dispatch:
 * app.setDispatch(dispatch);
 * await app.login({ username: 'user@example.com', password: 'secret' });
 * ```
 *
 * @see {@link AppProvider} for how the Application is integrated with React
 * @see {@link AppInstance} for the interface definition
 */
export class Application implements AppInstance {
  /**
   * React dispatch function for state updates.
   * @internal
   */
  private dispatch?: React.Dispatch<AppAction>;

  /** Application name from configuration */
  public name: string;

  /** Current environment (development, staging, production) */
  public env: string;

  /** Version information including release and branch */
  public version: AppVersion;

  /**
   * Creates a new Application instance.
   *
   * @param config - Configuration object containing app name, environment, and version
   *
   * @example
   * ```typescript
   * const app = new Application({
   *   name: 'LOA Frontend',
   *   env: 'development',
   *   version: { release: '1.0.0', branch: 'main' }
   * });
   * ```
   */
  constructor(config: AppConfig) {
    this.version = config.version;
    this.name = config.name;
    this.env = config.env;
  }

  /**
   * Sets the dispatch function for state management.
   *
   * @remarks
   * This method is called automatically by the {@link AppProvider} when it mounts.
   * It must be called before any state-modifying methods (login, logout, etc.)
   * can be used.
   *
   * @param dispatch - React dispatch function from useReducer
   *
   * @example
   * ```typescript
   * const [state, dispatch] = useReducer(appReducer, initialState);
   * app.setDispatch(dispatch);
   * ```
   */
  setDispatch(dispatch: React.Dispatch<AppAction>): void {
    this.dispatch = dispatch;
  }

  /**
   * Authenticates a user with the provided credentials.
   *
   * @remarks
   * Currently implements a mock authentication for development.
   * In production, this will integrate with the LOA backend authentication service.
   *
   * On success, dispatches `AUTH_LOGIN` with user data and token.
   * On failure, dispatches `AUTH_ERROR` with the error message.
   *
   * @param credentials - Object containing username and password
   * @returns Promise that resolves when authentication completes
   * @throws Error if dispatch is not initialized (setDispatch not called)
   *
   * @example
   * ```typescript
   * try {
   *   await app.login({
   *     username: 'user@company.com',
   *     password: 'securePassword'
   *   });
   *   // User is now authenticated
   * } catch (error) {
   *   // Handle dispatch not initialized error
   * }
   * ```
   */
  async login(credentials: UserCredentials): Promise<void> {
    if (!this.dispatch) throw new Error("Dispatch is not initialized");

    try {
      const { user, token } = await new Promise<{ user: User; token: string }>((resolve: any, reject: any) => {
        setTimeout(() => {
          if (credentials.passowrd !== "manu rocks") {
            reject(new Error("Unknown username or password"));
          } else {
            resolve({
              user: {
                id: "manu-rocks",
                avatar: "",
                name: "Manu Ramirez",
                username: "manuel.ramirez@mitratech.com",
              },
            });
          }
        });
      });

      this.dispatch({
        type: "AUTH_LOGIN",
        payload: {
          user,
          token,
        },
      });
    } catch (e: any) {
      this.dispatch({
        type: "AUTH_ERROR",
        payload: {
          error: e,
        },
      });
    }
  }

  /**
   * Logs out the current user and clears the session.
   *
   * @remarks
   * Dispatches `AUTH_LOGOUT` action which clears user data, token,
   * and sets `isAuthenticated` to false.
   *
   * @returns Promise that resolves when logout completes
   * @throws Error if dispatch is not initialized
   *
   * @example
   * ```typescript
   * await app.logout();
   * // User is now logged out, redirect to login page
   * ```
   */
  async logout(): Promise<void> {
    if (!this.dispatch) throw new Error("Dispatch is not initialized");

    this.dispatch({ type: "AUTH_LOGOUT" });
  }

  /**
   * Toggles the sidebar navigation open/closed state.
   *
   * @remarks
   * Dispatches `UI_TOGGLE_SIDEBAR` action which inverts the
   * `ui.isSidebarOpen` state value.
   *
   * @returns Promise that resolves when the toggle completes
   * @throws Error if dispatch is not initialized
   *
   * @example
   * ```typescript
   * // In a header component
   * const handleMenuClick = async () => {
   *   await app.toggleSidebar();
   * };
   * ```
   */
  async toggleSidebar(): Promise<void> {
    if (!this.dispatch) throw new Error("Dispatch is not initialized");

    this.dispatch({ type: "UI_TOGGLE_SIDEBAR" });
  }

  /**
   * Initializes the application on startup.
   *
   * @remarks
   * This method handles application bootstrap tasks including:
   * - Session restoration from localStorage
   * - Token validation and renewal
   * - Initial navigation based on auth state
   *
   * @param dispatch - Optional React dispatch function (deprecated, use setDispatch instead)
   * @returns Promise that resolves when initialization completes
   *
   * @example
   * ```typescript
   * // Called during app startup
   * await app.initialize();
   * ```
   */
  async initialize(dispatch?: React.Dispatch<AppAction>): Promise<void> {
    // this.setDispatch(dispatch);
    // dispatch({ type: "APP_START", payload: { name: this.name, env: this.env, version: this.version } });
    // @manu todo: read possible session store in LS
    // @manu todo: validate if token is still good, atempt to renew
    // @manu todo: if logged in, navigate to homepage
    // @manu todo: otherwise navigate to login
    // throw ("test");
  }
}
